import type { ExportGate } from "../api/types";

/**
 * Export downloading, and the ONE place that turns a refusal into a sentence.
 *
 * ── THE BUG THIS EXISTS FOR ────────────────────────────────────────────────
 * `GET /api/documents/:id/export` answers a refusal with a JSON body — `403
 * {"error":"delivery_gate_blocked", …}` when a required reviewer has not signed
 * off, `403 {"error":"forbidden"}` without the `export_deliver` capability, and
 * `500 {"error":"server_error"}` when the renderer itself fails.
 *
 * The download menu reached that route through a native `<a download>`, and **a
 * native `<a download>` cannot inspect a status code**. The browser writes
 * whatever bytes arrive to disk under the requested name, so the refusal was
 * saved as `Report.pdf` — a 299-byte JSON file wearing a PDF extension. Both
 * formats share the route, which is why PDF and DOCX broke identically.
 *
 * ── THE TWO LAYERS, AND WHY BOTH ──────────────────────────────────────────
 * 1. PRE-FLIGHT (`describeGate`): the document detail already carries the
 *    server's `export_gate`, so a refusal is known BEFORE the click. The menu
 *    renders a disabled item — with NO `href` at all — so no click, ⌘-click,
 *    middle-click, keyboard Enter or right-click "Save link as" can reach a
 *    route that would refuse. Removing the affordance is what actually fixes
 *    the reported bug.
 * 2. GUARDED FETCH (`downloadExport`): the pre-flight cannot predict everything
 *    — a reviewer's verdict can be stranded by an edit between page load and
 *    click, and a RENDER failure (`500`) is not predictable at all. Two live
 *    documents return exactly that 500 today. So the permitted path still
 *    verifies the response before writing a single byte to disk.
 *
 * Neither layer weakens the gate. The server re-decides on every request; this
 * module can only ever refuse to save, never cause a save that the server did
 * not authorise.
 */

/** A human label for a required-reviewer token, falling back to the raw name. */
const REVIEWER_LABELS: Readonly<Record<string, string>> = {
  "briefing-fidelity": "Briefing fidelity",
  quality: "Quality",
  seo: "SEO",
  "geo-leak-sourcing": "Geo-leak sourcing",
  "tone-grey-market": "Tone / grey-market",
};

export function reviewerLabel(name: string): string {
  return REVIEWER_LABELS[name] ?? name;
}

/** Join labels as prose: "A", "A and B", "A, B and C". */
function joinReviewers(names: readonly string[]): string {
  const labels = names.map(reviewerLabel);
  if (labels.length <= 1) return labels[0] ?? "";
  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
}

/**
 * The sentence a user sees for a refusal — used by BOTH the pre-flight (as the
 * disabled item's reason) and the guarded fetch (as the error surfaced after a
 * click), so the two can never explain the same refusal differently.
 *
 * `null` ⇔ nothing to explain (export is permitted, or no gate was supplied).
 */
export function describeGate(gate: ExportGate | null | undefined): string | null {
  if (!gate || gate.permitted) return null;
  if (gate.reason === "forbidden") {
    return "You do not have permission to export this document.";
  }
  if (gate.reason === "blocked") {
    return gate.missing.length > 0
      ? `Delivery is blocked — awaiting sign-off from ${joinReviewers(gate.missing)}.`
      : "Delivery is blocked — a required reviewer has not signed off on the current text.";
  }
  return "This document cannot be exported right now.";
}

/** The refusal body the export terminal sends. Every field is optional: a 500
 *  from an intermediary may not be our JSON at all. */
interface RefusalBody {
  error?: string;
  missing?: string[];
}

/** Turn a non-2xx export response into the same sentence `describeGate` gives. */
async function describeResponse(res: Response): Promise<string> {
  let body: RefusalBody = {};
  try {
    body = (await res.json()) as RefusalBody;
  } catch {
    // Not our JSON (proxy error page, truncated body). Fall through to status.
  }
  if (body.error === "delivery_gate_blocked") {
    return describeGate({
      permitted: false,
      reason: "blocked",
      missing: body.missing ?? [],
    })!;
  }
  if (body.error === "forbidden") {
    return describeGate({ permitted: false, reason: "forbidden", missing: [] })!;
  }
  if (res.status === 409) {
    return "This document has no exportable content yet.";
  }
  if (res.status >= 500) {
    return "The export couldn't be produced. Nothing was changed — try again in a moment.";
  }
  // ⛔ Never `(HTTP ${res.status})`. A status code is a diagnostic, not a
  // sentence — it tells the reader nothing they can act on and reads as a fault
  // report. The status is already in the network panel and the server log.
  return "The export was refused.";
}

/** RFC 6266 `filename="…"` (and `filename*=UTF-8''…`) off a Content-Disposition. */
export function filenameFromDisposition(disposition: string | null): string | null {
  if (!disposition) return null;
  const extended = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(disposition);
  if (extended) {
    try {
      return decodeURIComponent(extended[1].trim());
    } catch {
      // Malformed percent-encoding — fall back to the plain form below.
    }
  }
  const quoted = /filename\s*=\s*"([^"]*)"/i.exec(disposition);
  if (quoted) return quoted[1];
  const bare = /filename\s*=\s*([^;]+)/i.exec(disposition);
  return bare ? bare[1].trim() : null;
}

export type DownloadResult = { ok: true; filename: string } | { ok: false; message: string };

/** Injectable seams so the guarded path is testable without a real browser
 *  download (and so a test can assert that NOTHING was saved on a refusal). */
export interface DownloadDeps {
  fetch: typeof fetch;
  /** Commit the bytes to disk. Called ONLY after a verified 2xx. */
  save: (blob: Blob, filename: string) => void;
}

/** The real save: an object URL clicked through a detached anchor, then revoked. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke on a later turn: revoking synchronously can cancel the download in
  // WebKit before the browser has read the blob.
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

/**
 * Fetch an export and save it ONLY if the server really sent an artifact.
 *
 * The status check is the whole point: on any non-2xx the bytes are DISCARDED
 * and the reason is returned for the UI to show. Nothing reaches the disk.
 */
export async function downloadExport(
  url: string,
  fallbackFilename: string,
  deps: DownloadDeps = { fetch: globalThis.fetch.bind(globalThis), save: saveBlob },
): Promise<DownloadResult> {
  let res: Response;
  try {
    res = await deps.fetch(url, {
      credentials: "same-origin",
      headers: { Accept: "application/pdf, application/vnd.openxmlformats-officedocument.wordprocessingml.document" },
    });
  } catch {
    return { ok: false, message: "The export could not be reached — check your connection." };
  }

  if (!res.ok) return { ok: false, message: await describeResponse(res) };

  const filename =
    filenameFromDisposition(res.headers.get("content-disposition")) ?? fallbackFilename;
  const blob = await res.blob();
  deps.save(blob, filename);
  return { ok: true, filename };
}
